<style scoped>
/* The H3 field layers the Analysis pane has sent to the map, as rows in the
   Layers pane: the same visibility row the standing layers use (a checkbox, a
   swatch, the name), plus a trash button that takes the layer back off the map.
   The fill's checkbox and the trash are two different things — hiding the wash
   keeps the payload on the map, removing the layer drops it and flips the
   Analysis pane's own Send-to-map button back. The `.control-pane-*` names are
   the pane's own and are re-declared here because a scoped rule never reaches
   into another component's markup. */
.field-tray {
	margin-top: 8px;
	padding-top: 8px;
	border-top: 1px solid #f1f3f5;
}

.control-pane-title {
	font-weight: 600;
	margin-bottom: 6px;
}

.control-pane-row {
	display: flex;
	align-items: center;
	gap: 6px;
	line-height: 1.6;
	margin-top: 2px;
}

/* The visibility rows are labels wrapping a checkbox: clicking anywhere on the
   row toggles it, so the whole line is a pointer target. */
label.control-pane-row {
	cursor: pointer;
}

.control-pane-checkbox {
	margin: 0;
	flex-shrink: 0;
}

/* A hidden fill dims its swatch and label; the checkbox keeps full strength, so
   the control to bring the wash back stays obvious. */
.control-pane-row--hidden .control-pane-swatch,
.control-pane-row--hidden .control-pane-label {
	opacity: 0.4;
}

.control-pane-swatch {
	display: inline-block;
	width: 12px;
	height: 12px;
	border-radius: 50%;
	border: 2px solid #fff;
	box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.2);
	flex-shrink: 0;
}

/* A field swatch is the bivariate grid rather than one hue, so it is a
   gradient from the palette's falling corner to its rising corner. */
.field-tray-swatch {
	border-radius: 2px;
}

/* The name and the summary on their own lines, so a long summary reads rather
   than being cut off by the name's own width. Both ellipsise, and both carry
   their whole text in a title. */
.control-pane-label {
	flex: 1 1 auto;
	min-width: 0;
	display: flex;
	flex-direction: column;
}

.field-tray-name {
	font-weight: 600;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.field-tray-summary {
	color: #6c757d;
	font-size: 0.6875rem;
	line-height: 1.3;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

/* The trash: a button of its own on the row rather than a mode of the row, so
   removing the layer cannot be mistaken for hiding its fill. */
.field-tray-remove {
	flex: 0 0 auto;
	border: 1px solid #ced4da;
	background: #fff;
	border-radius: 0.25rem;
	padding: 2px 6px;
	font-size: 0.75rem;
	line-height: 1.4;
	color: #495057;
}

.field-tray-remove:hover {
	background: #f1f3f5;
	color: #b02a37;
}
</style>

<template>
	<!-- Nothing at all until a field is actually on the map: the section is a
	     reading of what was sent, not a place to discover what could be. -->
	<div v-if="store.populationOnMap || store.wasteOnMap" class="field-tray">
		<div class="control-pane-title">Field layers</div>

		<label
			v-if="store.populationOnMap"
			class="control-pane-row"
			:class="{ 'control-pane-row--hidden': !store.populationVisible }"
		>
			<input
				type="checkbox"
				class="control-pane-checkbox"
				:checked="store.populationVisible"
				aria-label="Population field on the map"
				@change="store.togglePopulationVisible()"
			/>
			<span
				class="control-pane-swatch field-tray-swatch"
				:style="{ background: bivariateGradient(POPULATION_BIV) }"
			></span>
			<span class="control-pane-label">
				<span class="field-tray-name" title="Population field"
					>Population field</span
				>
				<span class="field-tray-summary" :title="populationSummary">{{
					populationSummary
				}}</span>
			</span>
			<!-- The click must not reach the row's label: on a label row, a click
			     anywhere toggles the fill, and this button removes the layer. -->
			<button
				type="button"
				class="field-tray-remove"
				title="Remove the population field from the map"
				@click.prevent.stop="store.sendPopulationToMap(false)"
			>
				<i class="bi bi-trash"></i>
				<span class="visually-hidden"
					>Remove the population field from the map</span
				>
			</button>
		</label>

		<label
			v-if="store.wasteOnMap"
			class="control-pane-row"
			:class="{ 'control-pane-row--hidden': !store.wasteVisible }"
		>
			<input
				type="checkbox"
				class="control-pane-checkbox"
				:checked="store.wasteVisible"
				aria-label="Waste field on the map"
				@change="store.toggleWasteVisible()"
			/>
			<span
				class="control-pane-swatch field-tray-swatch"
				:style="{ background: bivariateGradient(WASTE_BIV) }"
			></span>
			<span class="control-pane-label">
				<span class="field-tray-name" title="Waste field">Waste field</span>
				<span class="field-tray-summary" :title="wasteSummary">{{
					wasteSummary
				}}</span>
			</span>
			<!-- As above: the trash removes the layer rather than toggling it. -->
			<button
				type="button"
				class="field-tray-remove"
				title="Remove the waste field from the map"
				@click.prevent.stop="store.sendWasteToMap(false)"
			>
				<i class="bi bi-trash"></i>
				<span class="visually-hidden">Remove the waste field from the map</span>
			</button>
		</label>
	</div>
</template>

<script setup lang="ts">
// The field layers the Analysis pane sends to the workbench map, as one section
// of the Layers pane. Two controls per row because they answer two questions:
// the checkbox hides the fill while keeping the payload on the map, and the
// trash takes the layer off entirely — which is the store's own `*OnMap` state,
// so the Analysis pane's Send-to-map button reads it back without a second copy
// here.
import { computed } from "vue";

import { useStoreMapReview } from "@/store/map-review";
import { POPULATION_BIV, WASTE_BIV } from "@/lib/mapReviewLayers";

const store = useStoreMapReview();

/** A bivariate palette as a swatch: the falling corner's lightest hue through
 * the stable middle to the rising corner's darkest, the diagonal the 3×3 grid
 * reads along. */
function bivariateGradient(palette: [string[], string[], string[]]): string {
	return `linear-gradient(135deg, ${palette[0][0]}, ${palette[1][1]} 50%, ${palette[2][2]})`;
}

/** What the population fill shows, in the map's own words. The resolution is
 * the payload's — the same number the Analysis pane's map caption prints — and
 * before the first read lands the row says so rather than inventing one. */
const populationSummary = computed(() => {
	const view = store.population;
	if (!view) return "waiting for the population field";
	return `H3 res ${view.resolution} · catch per trap × six-week slope`;
});

/** What the waste fill shows, in the map's own words. */
const wasteSummary = computed(() => {
	const view = store.waste;
	if (!view) return "waiting for the waste field";
	return `H3 res ${view.h3.resolution} · visit volume × treated share`;
});
</script>
